import { Button } from '@edsn/pie-components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { ToastButton } from '#pie/components/toast/Toast';
import { useToast } from '#pie/components/toast/ToastContext';
import type { CreateUpdateContactDetailsRequest } from '#pie/internal/api/pie-bff';
import {
  getContactDetails,
  getGetContactDetailsIdQueryKey,
  usePostContactDetailsCreate,
} from '#pie/internal/api/pie-bff';
import { FormOrganisationUser } from './FormOrganisationUser';

export const CreateOrganisationUser = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t: tcom } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');

  const [openActivateDialog, setOpenActivateDialog] = useState(false);
  const [email, setEmail] = useState<string>();

  const { mutate, isPending: isLoading } = usePostContactDetailsCreate({
    mutation: {
      onError: async (error) => {
        const errorCode = error as { code: string };
        switch (errorCode?.code) {
          case 'Contact.ActiveExists': {
            addToast({
              message: tom('toast.error.active_exists.message'),
              title: tom('toast.error.active_exists.title'),
              type: 'error',
              error: error,
            });
            break;
          }
          case 'Contact.InactiveExists': {
            setOpenActivateDialog(true);
            break;
          }
          default: {
            addToast({
              message: tom('toast.error.message.create_user'),
              title: tom('toast.error.title.create_user'),
              type: 'error',
              error: error,
            });
          }
        }
      },
      onSuccess: ({ id }) => {
        addToast({
          action: (
            <ToastButton>
              <Link to={`/beheer/gebruikersbeheer/${id}`}>
                {tom('toast.success.button.view_user')}
              </Link>
            </ToastButton>
          ),
          message: tom('toast.success.message.user_added'),
          title: tom('toast.success.title.user_created'),
          type: 'success',
        });
        queryClient.removeQueries({ queryKey: [getContactDetails.name] });
        queryClient.removeQueries({
          queryKey: getGetContactDetailsIdQueryKey(id),
        });
        navigate('/beheer/gebruikersbeheer/');
      },
    },
  });

  const onSubmit: SubmitHandler<CreateUpdateContactDetailsRequest> = (data) => {
    setEmail(data.email);
    mutate({ data: { ...data, activateIfInactive: openActivateDialog } });
  };

  return (
    <FormOrganisationUser
      onSubmit={onSubmit}
      isLoading={isLoading}
      confirmationDialog={
        <Dialog
          title={tom('dialog.activate.title.user_exists')}
          open={openActivateDialog}
          onOpenChange={setOpenActivateDialog}
          portal={false}
        >
          <Stack gap="md">
            <Text>
              {tom('dialog.activate.message.create', {
                email,
              })}
            </Text>
            <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
              <Button
                action="tertiary"
                emphasis="subtle"
                onClick={() => setOpenActivateDialog(false)}
              >
                {tcom('common.button.cancel')}
              </Button>
              <Button
                action="danger"
                emphasis="bold"
                type="submit"
                isLoading={isLoading}
              >
                {tom('dialog.activate.submit.activate_user')}
              </Button>
            </div>
          </Stack>
        </Dialog>
      }
    />
  );
};
